import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Plus, Pencil, Search, Settings2, ChevronDown, ChevronRight, RotateCcw, X, CheckSquare, Layers, Trash2, Eye, EyeOff, ArrowUpDown, GripVertical, Copy, LayoutList, LayoutGrid, Undo2, AlertTriangle } from "lucide-react";
import { JsonTemplateImport } from "@/components/admin/JsonTemplateImport";
import { useEditableCaseCategories } from "@/hooks/useEditableCaseCategories";
import { toast } from "@/hooks/use-toast";

export default function AdminCaseArticles() {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [categoryEditorOpen, setCategoryEditorOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchCategory, setBatchCategory] = useState("");
  const [sortBy, setSortBy] = useState<"date" | "name" | "status" | "manual">("date");
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [showTrash, setShowTrash] = useState(false);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const dragItemId = useRef<string | null>(null);
  const queryClient = useQueryClient();

  const {
    categories: editableCategories,
    updateCategory,
    addCategory,
    removeCategory,
    resetToDefaults,
    getCategoryName,
  } = useEditableCaseCategories();

  const { data: articles, isLoading } = useQuery({
    queryKey: ["admin-case-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_articles")
        .select("*")
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  // Split active vs trashed articles
  const activeArticles = (articles || []).filter((a) => !(a as any).deleted_at);
  const trashedArticles = (articles || []).filter((a) => (a as any).deleted_at);

  const filtered = activeArticles
    .filter((a) => {
      const matchesSearch =
        !search ||
        a.project_name.toLowerCase().includes(search.toLowerCase()) ||
        a.slug.toLowerCase().includes(search.toLowerCase());
      const matchesCategory =
        categoryFilter === "all" || (a as any).category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === "name") return a.project_name.localeCompare(b.project_name);
      if (sortBy === "status") return Number(b.published) - Number(a.published);
      if (sortBy === "manual") return ((a as any).display_order ?? 0) - ((b as any).display_order ?? 0);
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = () => {
    const list = showTrash ? trashedArticles : filtered;
    if (selected.size === list.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(list.map((a) => a.id)));
    }
  };

  const clearBatch = () => {
    setSelected(new Set());
    setBatchCategory("");
  };

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const promises = orderedIds.map((id, i) =>
        supabase.from("case_articles").update({ display_order: i } as any).eq("id", id)
      );
      const results = await Promise.all(promises);
      const failed = results.filter((r) => r.error);
      if (failed.length > 0) throw new Error(`${failed.length} updates failed`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-case-articles"] });
      toast({ title: "Order saved" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const batchUpdateMutation = useMutation({
    mutationFn: async (payload: { ids: string[]; updates: Record<string, any> }) => {
      const promises = payload.ids.map((id) =>
        supabase.from("case_articles").update(payload.updates).eq("id", id)
      );
      const results = await Promise.all(promises);
      const failed = results.filter((r) => r.error);
      if (failed.length > 0) throw new Error(`${failed.length} updates failed`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-case-articles"] });
      toast({ title: "Batch updated", description: `${selected.size} articles updated.` });
      clearBatch();
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Soft delete – set deleted_at timestamp
  const softDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from("case_articles")
        .update({ deleted_at: new Date().toISOString() } as any)
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-case-articles"] });
      toast({ title: "Moved to trash", description: `${selected.size} article(s) moved to trash.` });
      setSelected(new Set());
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Restore – clear deleted_at
  const restoreMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from("case_articles")
        .update({ deleted_at: null } as any)
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-case-articles"] });
      toast({ title: "Restored", description: "Article(s) restored from trash." });
      setSelected(new Set());
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  // Permanent delete
  const permanentDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await supabase.from("case_article_blocks").delete().eq("article_id", id);
        await supabase.from("case_article_materials").delete().eq("article_id", id);
        await supabase.from("case_article_gallery").delete().eq("article_id", id);
        await supabase.from("case_article_views").delete().eq("article_id", id);
      }
      const { error } = await supabase.from("case_articles").delete().in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-case-articles"] });
      toast({ title: "Permanently deleted", description: "Article(s) permanently removed." });
      setSelected(new Set());
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (articleId: string) => {
      const { data: original, error: fetchErr } = await supabase
        .from("case_articles")
        .select("*")
        .eq("id", articleId)
        .single();
      if (fetchErr || !original) throw fetchErr || new Error("Article not found");

      const { id, created_at, updated_at, ...fields } = original as any;
      const { deleted_at, ...cleanFields } = fields;
      const { data: newArticle, error: insertErr } = await supabase
        .from("case_articles")
        .insert({
          ...cleanFields,
          project_name: `${cleanFields.project_name} (Copy)`,
          slug: `${cleanFields.slug}-copy-${Date.now()}`,
          published: false,
        } as any)
        .select("id")
        .single();
      if (insertErr || !newArticle) throw insertErr || new Error("Insert failed");

      const { data: blocks } = await supabase
        .from("case_article_blocks")
        .select("*")
        .eq("article_id", articleId)
        .order("sort_order", { ascending: true });

      if (blocks && blocks.length > 0) {
        const clonedBlocks = blocks.map(({ id: _id, created_at: _ca, article_id: _aid, ...rest }) => ({
          ...rest,
          article_id: newArticle.id,
        }));
        const { error: blocksErr } = await supabase.from("case_article_blocks").insert(clonedBlocks);
        if (blocksErr) throw blocksErr;
      }

      return newArticle.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-case-articles"] });
      toast({ title: "Duplicated", description: "Article cloned as draft." });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const handleBatchApply = () => {
    if (!batchCategory) {
      toast({ title: "No changes", description: "Select a category to apply." });
      return;
    }
    batchUpdateMutation.mutate({
      ids: Array.from(selected),
      updates: { category: batchCategory },
    });
  };

  const handleBatchDelete = () => {
    if (confirm(`Move ${selected.size} article(s) to trash?`)) {
      softDeleteMutation.mutate(Array.from(selected));
    }
  };

  const handlePermanentDelete = () => {
    if (confirm(`Permanently delete ${selected.size} article(s)? This cannot be undone.`)) {
      permanentDeleteMutation.mutate(Array.from(selected));
    }
  };

  const handleRestore = () => {
    restoreMutation.mutate(Array.from(selected));
  };

  // Drag and drop handlers
  const handleDragStart = (id: string) => {
    dragItemId.current = id;
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    if (dragItemId.current !== id) {
      setDragOverId(id);
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragOverId(null);
    const sourceId = dragItemId.current;
    dragItemId.current = null;

    if (!sourceId || sourceId === targetId) return;

    const ids = filtered.map((a) => a.id);
    const fromIndex = ids.indexOf(sourceId);
    const toIndex = ids.indexOf(targetId);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...ids];
    reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, sourceId);

    reorderMutation.mutate(reordered);
  };

  const handleDragEnd = () => {
    dragItemId.current = null;
    setDragOverId(null);
  };

  const isManualSort = sortBy === "manual";

  return (
    <Layout>
      <div className="min-h-screen bg-muted">
        <div className="mx-auto max-w-[1200px] px-6 pt-24 pb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-serif text-2xl text-foreground">Case Articles</h1>
              <p className="text-sm text-muted-foreground mt-1">{activeArticles.length} total</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={showTrash ? "secondary" : "outline"}
                size="sm"
                className="gap-2 font-sans text-xs"
                onClick={() => { setShowTrash(!showTrash); setSelected(new Set()); }}
              >
                <Trash2 size={14} />
                Trash{trashedArticles.length > 0 && ` (${trashedArticles.length})`}
              </Button>
              <Button asChild size="sm" className="gap-2 font-sans text-xs uppercase tracking-wider">
                <Link to="/admin/cases/new">
                  <Plus size={14} /> New Article
                </Link>
              </Button>
            </div>
          </div>

          {showTrash ? (
            /* ===== TRASH VIEW ===== */
            <div>
              <div className="mb-6 flex items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                <AlertTriangle size={16} className="text-destructive shrink-0" />
                <p className="text-sm text-muted-foreground">
                  Trashed articles are hidden from the public site. Restore them or permanently delete.
                </p>
              </div>

              {trashedArticles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-12">Trash is empty.</p>
              ) : (
                <>
                  {/* Trash batch actions */}
                  <div className="mb-3 flex items-center gap-3">
                    <Checkbox
                      checked={selected.size === trashedArticles.length && trashedArticles.length > 0}
                      onCheckedChange={selectAll}
                    />
                    <span className="font-sans text-xs text-muted-foreground">Select all</span>
                    {selected.size > 0 && (
                      <>
                        <span className="text-xs text-muted-foreground">· {selected.size} selected</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1 text-xs"
                          onClick={handleRestore}
                          disabled={restoreMutation.isPending}
                        >
                          <Undo2 size={12} />
                          {restoreMutation.isPending ? "Restoring…" : "Restore"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1 text-xs text-destructive hover:bg-destructive/10"
                          onClick={handlePermanentDelete}
                          disabled={permanentDeleteMutation.isPending}
                        >
                          <Trash2 size={12} />
                          {permanentDeleteMutation.isPending ? "Deleting…" : "Delete Forever"}
                        </Button>
                      </>
                    )}
                  </div>

                  <div className="space-y-2">
                    {trashedArticles.map((article) => (
                      <div
                        key={article.id}
                        className={`flex items-center gap-4 border px-6 py-4 bg-background transition-all ${
                          selected.has(article.id) ? "border-primary/40 bg-primary/5" : "border-border"
                        }`}
                      >
                        <Checkbox
                          checked={selected.has(article.id)}
                          onCheckedChange={() => toggleSelect(article.id)}
                        />
                        <div className="h-14 w-20 rounded overflow-hidden bg-muted shrink-0">
                          {article.hero_image_url ? (
                            <img src={article.hero_image_url} alt={article.project_name} className="h-full w-full object-cover" loading="lazy" />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-muted-foreground/50 text-[10px]">No img</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-sans text-sm font-medium text-foreground">{article.project_name}</p>
                          <p className="font-sans text-xs text-muted-foreground mt-1">
                            Deleted {new Date((article as any).deleted_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1 text-xs"
                            onClick={() => restoreMutation.mutate([article.id])}
                            disabled={restoreMutation.isPending}
                          >
                            <Undo2 size={14} /> Restore
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1 text-xs text-destructive hover:text-destructive"
                            onClick={() => {
                              if (confirm(`Permanently delete "${article.project_name}"? This cannot be undone.`)) {
                                permanentDeleteMutation.mutate([article.id]);
                              }
                            }}
                            disabled={permanentDeleteMutation.isPending}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* ===== MAIN VIEW ===== */
            <>
              {/* Category Editor */}
              <div className="mb-6">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-xs text-muted-foreground mb-3"
                  onClick={() => setCategoryEditorOpen(!categoryEditorOpen)}
                >
                  <Settings2 size={14} />
                  Manage Categories
                  {categoryEditorOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </Button>

                {categoryEditorOpen && (
                  <div className="rounded-lg border border-border bg-background p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">Rename, add, or remove project categories.</p>
                      <Button variant="ghost" size="sm" className="gap-1 text-xs text-muted-foreground" onClick={resetToDefaults}>
                        <RotateCcw size={12} /> Reset
                      </Button>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {editableCategories.map((cat) => (
                        <div key={cat.id} className="flex items-center gap-1.5">
                          <Input
                            value={cat.name}
                            onChange={(e) => updateCategory(cat.id, e.target.value)}
                            className="h-8 text-sm flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={() => removeCategory(cat.id)}
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-2 max-w-xs">
                      <Input
                        value={newCatName}
                        onChange={(e) => setNewCatName(e.target.value)}
                        placeholder="New category…"
                        className="h-8 text-sm flex-1"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && newCatName.trim()) {
                            addCategory(newCatName.trim());
                            setNewCatName("");
                          }
                        }}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-primary"
                        onClick={() => {
                          if (newCatName.trim()) {
                            addCategory(newCatName.trim());
                            setNewCatName("");
                          }
                        }}
                      >
                        <Plus size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Search + Sort */}
              <div className="mb-6 flex flex-wrap items-center gap-3">
                <div className="relative max-w-md flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search articles..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-background"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <ArrowUpDown size={14} className="text-muted-foreground" />
                  {(["date", "name", "status", "manual"] as const).map((s) => (
                    <Button
                      key={s}
                      variant={sortBy === s ? "secondary" : "ghost"}
                      size="sm"
                      className="h-8 text-xs font-sans capitalize"
                      onClick={() => setSortBy(s)}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>

              {isManualSort && (
                <p className="mb-4 text-xs text-muted-foreground flex items-center gap-1.5">
                  <GripVertical size={12} /> Drag articles to reorder. Changes save automatically.
                </p>
              )}

              {/* Category Filter Bar */}
              <div className="mb-6 flex flex-wrap items-center gap-2">
                <Button
                  variant={categoryFilter === "all" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-8 text-xs font-sans"
                  onClick={() => setCategoryFilter("all")}
                >
                  All ({activeArticles.length})
                </Button>
                {editableCategories.map((cat) => {
                  const count = activeArticles.filter((a) => (a as any).category === cat.id).length;
                  return (
                    <Button
                      key={cat.id}
                      variant={categoryFilter === cat.id ? "secondary" : "ghost"}
                      size="sm"
                      className="h-8 text-xs font-sans"
                      onClick={() => setCategoryFilter(cat.id)}
                    >
                      {cat.name} ({count})
                    </Button>
                  );
                })}
              </div>

              {/* Batch Action Bar */}
              {selected.size > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <CheckSquare size={16} className="text-primary" />
                    <span>{selected.size} selected</span>
                  </div>

                  <div className="h-5 w-px bg-border" />

                  <Select value={batchCategory} onValueChange={setBatchCategory}>
                    <SelectTrigger className="h-8 w-[180px] text-xs bg-background">
                      <SelectValue placeholder="Set category…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uncategorized">Uncategorized</SelectItem>
                      {editableCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button
                    size="sm"
                    className="h-8 gap-1 text-xs"
                    onClick={handleBatchApply}
                    disabled={batchUpdateMutation.isPending}
                  >
                    <Layers size={12} />
                    {batchUpdateMutation.isPending ? "Applying…" : "Apply"}
                  </Button>

                  <div className="h-5 w-px bg-border" />

                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1 text-xs"
                    onClick={() => batchUpdateMutation.mutate({ ids: Array.from(selected), updates: { published: true } })}
                    disabled={batchUpdateMutation.isPending}
                  >
                    <Eye size={12} /> Publish
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1 text-xs"
                    onClick={() => batchUpdateMutation.mutate({ ids: Array.from(selected), updates: { published: false } })}
                    disabled={batchUpdateMutation.isPending}
                  >
                    <EyeOff size={12} /> Unpublish
                  </Button>

                  <div className="h-5 w-px bg-border" />

                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1 text-xs text-destructive hover:bg-destructive/10"
                    onClick={handleBatchDelete}
                    disabled={softDeleteMutation.isPending}
                  >
                    <Trash2 size={12} />
                    {softDeleteMutation.isPending ? "Moving…" : "Move to Trash"}
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs text-muted-foreground"
                    onClick={clearBatch}
                  >
                    Clear
                  </Button>
                </div>
              )}

              {/* Import */}
              <div className="mb-8">
                <p className="font-sans text-xs text-muted-foreground mb-2 uppercase tracking-wider">JSON Import</p>
                <JsonTemplateImport />
              </div>

              {isLoading && (
                <p className="font-sans text-muted-foreground">Loading…</p>
              )}

              {/* Select All + View Toggle */}
              {filtered.length > 0 && (
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selected.size === filtered.length && filtered.length > 0}
                      onCheckedChange={selectAll}
                    />
                    <span className="font-sans text-xs text-muted-foreground">Select all</span>
                  </div>
                  <div className="flex items-center gap-1 border border-border rounded-md p-0.5">
                    <Button
                      variant={viewMode === "list" ? "secondary" : "ghost"}
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setViewMode("list")}
                    >
                      <LayoutList size={14} />
                    </Button>
                    <Button
                      variant={viewMode === "grid" ? "secondary" : "ghost"}
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setViewMode("grid")}
                    >
                      <LayoutGrid size={14} />
                    </Button>
                  </div>
                </div>
              )}

              {viewMode === "list" ? (
                <div className="space-y-2">
                  {filtered.map((article) => (
                    <div
                      key={article.id}
                      draggable={isManualSort}
                      onDragStart={() => handleDragStart(article.id)}
                      onDragOver={(e) => handleDragOver(e, article.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, article.id)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-4 border px-6 py-4 bg-background transition-all ${
                        selected.has(article.id)
                          ? "border-primary/40 bg-primary/5"
                          : dragOverId === article.id
                          ? "border-primary border-dashed bg-primary/5"
                          : "border-border"
                      } ${isManualSort ? "cursor-grab active:cursor-grabbing" : ""}`}
                    >
                      {isManualSort && (
                        <GripVertical size={16} className="text-muted-foreground shrink-0" />
                      )}
                      <Checkbox
                        checked={selected.has(article.id)}
                        onCheckedChange={() => toggleSelect(article.id)}
                      />
                      <div className="h-14 w-20 rounded overflow-hidden bg-muted shrink-0">
                        {article.hero_image_url ? (
                          <img
                            src={article.hero_image_url}
                            alt={article.project_name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-muted-foreground/50 text-[10px]">
                            No img
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-sm font-medium text-foreground">
                          {article.project_name}
                        </p>
                        <p className="font-sans text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                          /{article.slug} ·{" "}
                          <button
                            onClick={(e) => { e.stopPropagation(); batchUpdateMutation.mutate({ ids: [article.id], updates: { published: !article.published } }); }}
                            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
                            title={article.published ? "Click to unpublish" : "Click to publish"}
                          >
                            <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${article.published ? "bg-green-500" : "bg-muted-foreground/40"}`} />
                            {article.published ? "Published" : "Draft"}
                          </button>
                          {(article as any).category && (article as any).category !== "uncategorized" && (
                            <span> · {getCategoryName((article as any).category)}</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => duplicateMutation.mutate(article.id)}
                          disabled={duplicateMutation.isPending}
                          title="Duplicate"
                        >
                          <Copy size={16} />
                        </Button>
                        <Button asChild variant="ghost" size="icon">
                          <Link to={`/admin/cases/${article.id}/edit`}>
                            <Pencil size={16} />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filtered.map((article) => (
                    <div
                      key={article.id}
                      className={`group relative border rounded-lg overflow-hidden bg-background transition-all ${
                        selected.has(article.id) ? "border-primary/40 ring-1 ring-primary/20" : "border-border"
                      }`}
                    >
                      {/* Selection checkbox */}
                      <div className="absolute top-3 left-3 z-10">
                        <Checkbox
                          checked={selected.has(article.id)}
                          onCheckedChange={() => toggleSelect(article.id)}
                          className="bg-background/80 backdrop-blur-sm"
                        />
                      </div>
                      {/* Hero image */}
                      <div className="aspect-[16/10] bg-muted overflow-hidden">
                        {article.hero_image_url ? (
                          <img
                            src={article.hero_image_url}
                            alt={article.project_name}
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-muted-foreground/40 text-sm">
                            No image
                          </div>
                        )}
                      </div>
                      {/* Card content */}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-sans text-sm font-medium text-foreground truncate">
                              {article.project_name}
                            </p>
                            <button
                              onClick={() => batchUpdateMutation.mutate({ ids: [article.id], updates: { published: !article.published } })}
                              className="font-sans text-xs text-muted-foreground mt-1 flex items-center gap-1.5 hover:text-foreground transition-colors"
                              title={article.published ? "Click to unpublish" : "Click to publish"}
                            >
                              <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${article.published ? "bg-green-500" : "bg-muted-foreground/40"}`} />
                              {article.published ? "Published" : "Draft"}
                              {(article as any).category && (article as any).category !== "uncategorized" && (
                                <span> · {getCategoryName((article as any).category)}</span>
                              )}
                            </button>
                          </div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => duplicateMutation.mutate(article.id)}
                              disabled={duplicateMutation.isPending}
                              title="Duplicate"
                            >
                              <Copy size={14} />
                            </Button>
                            <Button asChild variant="ghost" size="icon" className="h-7 w-7">
                              <Link to={`/admin/cases/${article.id}/edit`}>
                                <Pencil size={14} />
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
